import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  winbackAdminService,
  WinbackCronResult,
  WinbackManualHistoryItem,
  WinbackOverview,
  WinbackProspect,
} from '../services/apiService';

/**
 * Abone Kazanımı — iOS
 *
 * İki sekme:
 *  1. Abone Olmayanlar — uygulamayı kullanan ama abone olmayan kullanıcılar;
 *     her birine tek tek kişiye özel bildirim gönderilebilir.
 *  2. Otomatik Kampanya — winback kampanyasının durumu ve hunisi.
 *
 * Koruma kuralları sunucudadır (aktif aboneye gönderilmez, bildirim izni yoksa
 * gönderilmez, kişi başına günlük/toplam sınır). Bu ekran onları aşamaz.
 */

const PAGE_SIZE = 50;

type Tab = 'prospects' | 'campaign';

const AdminWinback: React.FC = () => {
  const [tab, setTab] = useState<Tab>('prospects');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Abone Kazanımı</h1>
        <p className="mt-1 text-sm text-gray-500">
          Uygulamayı kullanan ama abone olmayan kullanıcıları görün ve tek tek bildirim gönderin.
        </p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        <TabButton active={tab === 'prospects'} onClick={() => setTab('prospects')}>
          Abone Olmayanlar
        </TabButton>
        <TabButton active={tab === 'campaign'} onClick={() => setTab('campaign')}>
          Otomatik Kampanya
        </TabButton>
      </div>

      {tab === 'prospects' ? <ProspectsTab /> : <CampaignTab />}
    </div>
  );
};

/* ========================================================================== */
/*  Sekme 1 — Abone olmayanlar                                                */
/* ========================================================================== */

const ProspectsTab: React.FC = () => {
  const { token } = useAuth();

  const [rows, setRows] = useState<WinbackProspect[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [onlyNotifiable, setOnlyNotifiable] = useState(true);
  const [minDays, setMinDays] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<WinbackProspect | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await winbackAdminService.prospects(token, {
        search: search || undefined,
        onlyNotifiable,
        minDaysSinceInstall: minDays,
        page,
        pageSize: PAGE_SIZE,
      });
      setRows(res.data?.items ?? []);
      setTotal(res.data?.totalCount ?? 0);
    } catch (e: any) {
      setError(e?.message || 'Liste alınamadı');
    } finally {
      setLoading(false);
    }
  }, [token, search, onlyNotifiable, minDays, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Filtreler */}
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-4">
        <input
          type="text"
          placeholder="E-posta veya isim ara..."
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          className="w-64 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        />

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={onlyNotifiable}
            onChange={(e) => {
              setPage(1);
              setOnlyNotifiable(e.target.checked);
            }}
            className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
          />
          Sadece bildirim gönderilebilenler
        </label>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          En az
          <input
            type="number"
            min={0}
            value={minDays}
            onChange={(e) => {
              setPage(1);
              setMinDays(Number(e.target.value));
            }}
            className="w-16 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-purple-500 focus:outline-none"
          />
          gün önce kurmuş
        </label>

        <button
          onClick={load}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          Yenile
        </button>

        <span className="ml-auto text-sm text-gray-500">{total} kullanıcı</span>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      {loading ? (
        <p className="text-sm text-gray-500">Yükleniyor...</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-gray-500">Kayıt bulunamadı.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Kullanıcı</Th>
                <Th>Üyelik</Th>
                <Th>Kurulum</Th>
                <Th>Son görülme</Th>
                <Th>Platform</Th>
                <Th>Yerel saat</Th>
                <Th>Gönderilen</Th>
                <Th>Durum</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rows.map((r) => (
                <tr key={r.userEmail} className="hover:bg-gray-50">
                  <Td>
                    <div className="font-medium text-gray-900">{r.userName || '(isimsiz)'}</div>
                    <div className="text-xs text-gray-500">{r.userEmail}</div>
                  </Td>
                  <Td>{r.membershipType}</Td>
                  <Td>{r.daysSinceInstall} gün önce</Td>
                  <Td>{r.daysSinceLastSeen === null ? '—' : `${r.daysSinceLastSeen} gün önce`}</Td>
                  <Td>{r.availablePlatforms || '—'}</Td>
                  <Td>
                    <span className={r.withinSendWindow ? 'text-green-600' : 'text-red-600'}>
                      {String(r.localHour).padStart(2, '0')}:00
                    </span>
                  </Td>
                  <Td>
                    {r.manualNotificationsSent} manuel
                    {r.campaignNotificationsSent > 0 && (
                      <span className="text-gray-500"> + {r.campaignNotificationsSent} kampanya</span>
                    )}
                  </Td>
                  <Td>
                    {r.canSendManualNow ? (
                      <span className="text-green-600">Gönderilebilir</span>
                    ) : (
                      <span className="text-xs text-red-600">{r.blockedReason}</span>
                    )}
                  </Td>
                  <Td>
                    <button
                      onClick={() => setSelected(r)}
                      disabled={!r.canSendManualNow}
                      className="rounded-md bg-purple-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                    >
                      Bildirim yaz
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            ← Önceki
          </button>
          <span className="text-sm text-gray-600">
            Sayfa {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-40"
          >
            Sonraki →
          </button>
        </div>
      )}

      {selected && (
        <SendModal
          prospect={selected}
          onClose={() => setSelected(null)}
          onDone={() => {
            setSelected(null);
            load();
          }}
        />
      )}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*  Kişiye özel bildirim penceresi                                            */
/* -------------------------------------------------------------------------- */

const SendModal: React.FC<{
  prospect: WinbackProspect;
  onClose: () => void;
  onDone: () => void;
}> = ({ prospect, onClose, onDone }) => {
  const { token } = useAuth();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [ignoreWindow, setIgnoreWindow] = useState(false);
  const [platform, setPlatform] = useState<'android' | 'ios' | ''>(
    prospect.hasAndroidDevice && !prospect.hasIosDevice
      ? 'android'
      : prospect.hasIosDevice && !prospect.hasAndroidDevice
      ? 'ios'
      : ''
  );
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [history, setHistory] = useState<WinbackManualHistoryItem[]>([]);

  useEffect(() => {
    if (!token) return;
    winbackAdminService
      .manualHistory(token, prospect.userEmail)
      .then((r) => setHistory(r.data ?? []))
      .catch(() => setHistory([]));
  }, [token, prospect.userEmail]);

  const firstName = (prospect.userName || '').trim().split(' ')[0];

  // Şablonlar başlangıç noktasıdır; kişiselleştirip gönderin.
  // Fiyat/indirim oranı YAZMAYIN — gerçek fiyat App Store'dan gelir.
  const templates = [
    {
      label: 'Değer hatırlatma',
      title: 'Bugün kendin için küçük bir adım at 🌱',
      body: `${firstName ? `${firstName}, s` : 'S'}ana özel günlük motivasyonunu görmek için dokun.`,
    },
    {
      label: 'Premium tanıtım',
      title: "DailyPositive'ı daha güçlü kullan",
      body: 'Premium içerikler ve daha kişisel bir deneyim seni bekliyor.',
    },
    {
      label: 'İlk ay fırsatı',
      title: 'Sana özel ilk ay fırsatı 🎁',
      body: 'Premium teklifini görmek için dokun.',
    },
  ];

  const submit = async () => {
    if (!token) return;
    setSending(true);
    setError(null);
    setInfo(null);
    try {
      const res = await winbackAdminService.sendManual(token, {
        email: prospect.userEmail,
        title: title.trim(),
        body: body.trim(),
        platform: platform as 'android' | 'ios',
        ignoreSendWindow: ignoreWindow,
      });
      const d = res.data;
      setInfo(
        `${d?.message ?? 'Gönderildi.'} Kalan: bugün ${d?.remainingToday ?? 0}, toplam ${
          d?.remainingTotal ?? 0
        }`
      );
      setTimeout(onDone, 1200);
    } catch (e: any) {
      setError(e?.message || 'Bildirim gönderilemedi');
    } finally {
      setSending(false);
    }
  };

  const handleOptOut = async () => {
    if (!token) return;
    if (!window.confirm('Bu kullanıcı kampanyadan çıkarılsın mı? Bir daha bildirim gönderilemez.'))
      return;
    try {
      await winbackAdminService.optOut(token, prospect.userEmail);
      onDone();
    } catch (e: any) {
      setError(e?.message || 'İşlem başarısız');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900">Bildirim gönder</h2>
        <p className="mt-1 text-sm text-gray-500">
          {prospect.userName || '(isimsiz)'} — {prospect.userEmail}
          <br />
          Kurulum: {prospect.daysSinceInstall} gün önce · Yerel saat:{' '}
          {String(prospect.localHour).padStart(2, '0')}:00 · Üyelik: {prospect.membershipType}
          <br />
          Aktif cihaz: {prospect.availablePlatforms || 'yok'}
        </p>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Bildirimin gönderileceği platform
        </label>
        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value as 'android' | 'ios' | '')}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Platform seçin</option>
          {prospect.hasAndroidDevice && <option value="android">Android — Google Play teklifi</option>}
          {prospect.hasIosDevice && <option value="ios">iOS — App Store teklifi</option>}
        </select>

        {!prospect.withinSendWindow && (
          <div className="mt-3">
            <Alert kind="warn">
              Kullanıcının yerel saati gönderim penceresi dışında. Yine de göndermek için aşağıdaki
              kutuyu işaretleyin.
            </Alert>
          </div>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {templates.map((t) => (
            <button
              key={t.label}
              onClick={() => {
                setTitle(t.title);
                setBody(t.body);
              }}
              className="rounded-md border border-gray-300 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              {t.label}
            </button>
          ))}
        </div>

        <label className="mt-4 block text-sm font-medium text-gray-700">
          Başlık
          <span className="ml-1 font-normal text-gray-400">({title.length}/100)</span>
        </label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={100}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        />

        <label className="mt-3 block text-sm font-medium text-gray-700">
          Mesaj
          <span className="ml-1 font-normal text-gray-400">({body.length}/500)</span>
        </label>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={500}
          rows={4}
          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
        />

        <p className="mt-2 text-xs text-gray-500">
          Bildirime dokunan kullanıcı Premium teklif ekranına gider. Mesajda fiyat veya indirim oranı
          yazmayın — gerçek fiyat App Store'dan gelir, uyuşmazsa yanıltıcı olur.
        </p>

        {!prospect.withinSendWindow && (
          <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={ignoreWindow}
              onChange={(e) => setIgnoreWindow(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            Sessiz saati yoksay (kullanıcının gecesinde gönder)
          </label>
        )}

        {error && (
          <div className="mt-3">
            <Alert kind="error">{error}</Alert>
          </div>
        )}
        {info && (
          <div className="mt-3">
            <Alert kind="success">{info}</Alert>
          </div>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            onClick={submit}
          disabled={sending || !title.trim() || !body.trim() || !platform}
            className="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {sending ? 'Gönderiliyor...' : 'Gönder'}
          </button>
          <button
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Vazgeç
          </button>
          <button
            onClick={handleOptOut}
            className="ml-auto text-sm text-red-600 hover:underline"
          >
            Kampanyadan çıkar
          </button>
        </div>

        {history.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold text-gray-900">Bu kişiye gönderilenler</h3>
            <div className="mt-2 divide-y divide-gray-200">
              {history.map((h, i) => (
                <div key={i} className="py-2 text-sm">
                  <div className="font-medium text-gray-800">{h.title}</div>
                  <div className="text-gray-600">{h.body}</div>
                  <div className="text-xs text-gray-400">
                    {new Date(h.sentAt).toLocaleString('tr-TR')} ·{' '}
                    {h.sendSucceeded ? 'gönderildi' : `başarısız (${h.failureReason || '-'})`}
                    {h.sentByAdmin ? ` · ${h.sentByAdmin}` : ''}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ========================================================================== */
/*  Sekme 2 — Otomatik kampanya                                               */
/* ========================================================================== */

const CampaignTab: React.FC = () => {
  const { token } = useAuth();

  const [data, setData] = useState<WinbackOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runResult, setRunResult] = useState<WinbackCronResult | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await winbackAdminService.overview(token);
      setData(res.data ?? null);
    } catch (e: any) {
      setError(e?.message || 'Özet alınamadı');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (dryRun: boolean) => {
    if (!token) return;
    if (
      !dryRun &&
      !window.confirm(
        'GERÇEK gönderim yapılacak. Uygun tüm kullanıcılara bildirim gidecek. Emin misiniz?'
      )
    ) {
      return;
    }
    setRunning(true);
    setError(null);
    try {
      const res = await winbackAdminService.run(token, dryRun);
      setRunResult(res.data ?? null);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Çalıştırılamadı');
    } finally {
      setRunning(false);
    }
  };

  if (loading) return <p className="text-sm text-gray-500">Yükleniyor...</p>;
  if (error) return <Alert kind="error">{error}</Alert>;
  if (!data) return null;

  const s = data.settings;

  return (
    <div className="space-y-4">
      {data.warnings.map((w, i) => (
        <Alert key={i} kind="warn">
          {w}
        </Alert>
      ))}

      <Card title="Durum">
        <div className="flex flex-wrap gap-8">
          <Flag label="Kampanya" on={s.enabled} />
          <Flag label="Test modu" on={s.testMode} neutral />
          <Flag label="Gerçek gönderim" on={s.realSendEnabled} />
        </div>
        <p className="mt-3 text-sm text-gray-600">
          Ürün: {s.productId} · Etiket: {s.offerTag} · Pencere: {s.sendWindowLocal} · Maks.{' '}
          {s.maxNotificationsPerUser} bildirim, arada en az {s.minHoursBetweenNotifications} saat ·
          Adımlar: {s.stepDelayHours.join(' / ')} saat
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Bu ayarlar sunucu yapılandırmasından gelir; panelden değiştirilemez.
        </p>
      </Card>

      <Card title="Huni">
        <div className="flex flex-wrap gap-8">
          <Stat label="Kampanyadaki" value={data.totalUsersInCampaign} />
          <Stat label="Aktif" value={data.activeUsers} />
          <Stat label="Tamamlanan" value={data.completedUsers} />
          <Stat label="Çıkan" value={data.optedOutUsers} />
          <Stat label="Abone olan" value={data.subscribedUsers} highlight />
          <Stat label="Gönderilen" value={data.notificationsSentTotal} />
          <Stat label="Başarısız" value={data.notificationsFailedTotal} />
        </div>
        {data.lastNotificationSentAt && (
          <p className="mt-3 text-sm text-gray-500">
            Son gönderim: {new Date(data.lastNotificationSentAt).toLocaleString('tr-TR')}
          </p>
        )}
      </Card>

      {data.steps.length > 0 && (
        <Card title="Adım bazlı">
          <SimpleTable
            head={['Adım', 'Varyant', 'Gönderilen', 'Başarısız']}
            rows={data.steps.map((st) => [
              String(st.step),
              st.notificationVariant,
              String(st.sent),
              String(st.failed),
            ])}
          />
        </Card>
      )}

      {data.variants.length > 0 && (
        <Card title="A/B varyantları">
          <SimpleTable
            head={['Varyant', 'İndirim', 'Kullanıcı', 'Abone', 'Dönüşüm']}
            rows={data.variants.map((v) => [
              v.variant,
              v.showsDiscount ? 'Gösteriliyor' : 'Kontrol grubu',
              String(v.users),
              String(v.subscribed),
              `%${v.conversionPercent}`,
            ])}
          />
        </Card>
      )}

      <Card title="Kampanya turunu çalıştır">
        <p className="text-sm text-gray-600">
          Kuru çalışma hiçbir bildirim göndermez; kimin uygun olduğunu ve kimin neden elendiğini
          gösterir. Önce bunu çalıştırın.
        </p>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => run(true)}
            disabled={running}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {running ? 'Çalışıyor...' : 'Kuru çalışma'}
          </button>
          <button
            onClick={() => run(false)}
            disabled={running || !s.enabled || !s.realSendEnabled}
            title={
              !s.enabled
                ? 'Kampanya kapalı'
                : !s.realSendEnabled
                ? 'Gerçek gönderim sunucuda kapalı'
                : undefined
            }
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            Gerçek gönderim
          </button>
        </div>

        {runResult && (
          <div className="mt-4 text-sm text-gray-700">
            <div>
              Aday: {runResult.candidateCount} · Gönderilen: {runResult.sentCount} · Kuru:{' '}
              {runResult.dryRunCount} · Atlanan: {runResult.skippedCount} · Hata:{' '}
              {runResult.errorCount}
            </div>
            {runResult.message && <div className="text-red-600">{runResult.message}</div>}
            {Object.keys(runResult.skipReasons || {}).length > 0 && (
              <div className="mt-2">
                <div className="font-medium">Elenme nedenleri</div>
                <ul className="mt-1 list-inside list-disc text-gray-600">
                  {Object.entries(runResult.skipReasons).map(([k, v]) => (
                    <li key={k}>
                      {k}: {v}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

/* ========================================================================== */
/*  Yardımcı bileşenler                                                       */
/* ========================================================================== */

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active,
  onClick,
  children,
}) => (
  <button
    onClick={onClick}
    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
      active
        ? 'border-purple-600 text-purple-700'
        : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >
    {children}
  </button>
);

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-5">
    <h3 className="mb-3 text-base font-semibold text-gray-900">{title}</h3>
    {children}
  </div>
);

const Stat: React.FC<{ label: string; value: number; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div>
    <div className={`text-2xl font-semibold ${highlight ? 'text-green-600' : 'text-gray-900'}`}>
      {value}
    </div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

const Flag: React.FC<{ label: string; on: boolean; neutral?: boolean }> = ({
  label,
  on,
  neutral,
}) => (
  <div>
    <div
      className={`text-base font-semibold ${
        neutral ? 'text-amber-600' : on ? 'text-green-600' : 'text-red-600'
      }`}
    >
      {on ? 'Açık' : 'Kapalı'}
    </div>
    <div className="text-sm text-gray-500">{label}</div>
  </div>
);

const Alert: React.FC<{ kind: 'error' | 'warn' | 'success'; children: React.ReactNode }> = ({
  kind,
  children,
}) => {
  const cls =
    kind === 'error'
      ? 'border-red-300 bg-red-50 text-red-700'
      : kind === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-800'
      : 'border-green-300 bg-green-50 text-green-700';

  return <div className={`rounded-md border px-4 py-2.5 text-sm ${cls}`}>{children}</div>;
};

const SimpleTable: React.FC<{ head: string[]; rows: string[][] }> = ({ head, rows }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          {head.map((h) => (
            <Th key={h}>{h}</Th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-200">
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <Td key={j}>{c}</Td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Th: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
    {children}
  </th>
);

const Td: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <td className="px-4 py-3 align-top text-sm text-gray-700">{children}</td>
);

export default AdminWinback;
